import React from 'react';
import { ResponsiveComponentWrapper, ResponsiveHeader, ResponsiveRoomCard } from '../components/ResponsiveComponentWrapper';
import ResponsiveHookTester from '../components/ResponsiveHookTester';

/**
 * ResponsiveTestPage - Test component to demonstrate responsive switching
 * 
 * This component shows:
 * 1. Direct ResponsiveComponentWrapper usage
 * 2. Convenience wrapper components  
 * 3. CSS responsive classes
 * 4. Breakpoint behavior
 */
const ResponsiveTestPage: React.FC = () => {
  const testHeaderProps = {
    title: "Test Application",
    userName: "Test User",
    onLogout: () => console.log("Logout clicked")
  };

  const testRoomProps = {
    roomId: "test-room-123",
    roomName: "Test Room",
    playerCount: 3,
    maxPlayers: 4,
    gameType: "Kadi ni Tidi",
    onJoinRoom: () => console.log("Join room clicked")
  };

  return (
    <div className="responsive-test-page">
      <h1>🧪 Responsive System Testing</h1>
      <p>Resize your browser or use DevTools device emulation to test responsive behavior!</p>

      {/* Live Hook Testing */}
      <ResponsiveHookTester />

      {/* Test 1: Responsive Header */}
      <section className="test-section">
        <h2>Test 1: Responsive Header</h2>
        <div className="test-container">
          <ResponsiveHeader headerProps={testHeaderProps} />
        </div>
      </section>

      {/* Test 2: Responsive Room Card */}
      <section className="test-section">
        <h2>Test 2: Responsive Room Card</h2>
        <div className="test-container">
          <ResponsiveRoomCard roomCardProps={testRoomProps} />
        </div>
      </section>

      {/* Test 3: Manual ResponsiveWrapper */}
      <section className="test-section">
        <h2>Test 3: Custom Responsive Content</h2>
        <ResponsiveComponentWrapper
          mobileComponent={
            <div className="mobile-test-box">
              <h3>📱 Mobile View</h3>
              <p>This is the mobile version</p>
              <button className="game-btn touch-friendly">Mobile Action</button>
            </div>
          }
          desktopComponent={
            <div className="desktop-test-box">
              <h3>🖥️ Desktop View</h3>
              <p>This is the desktop version with more space</p>
              <div className="desktop-actions">
                <button className="game-btn">Desktop Action 1</button>
                <button className="game-btn secondary">Desktop Action 2</button>
              </div>
            </div>
          }
        />
      </section>

      {/* Test 4: CSS Responsive Classes */}
      <section className="test-section">
        <h2>Test 4: CSS Responsive Classes</h2>
        <div className="mobile-only">
          <p>🔥 This only shows on mobile/tablet (width &lt; 1024px)</p>
        </div>
        <div className="desktop-only">
          <p>💻 This only shows on desktop (width ≥ 1024px)</p>
        </div>
      </section>

      {/* Test 5: Breakpoint Information */}
      <section className="test-section">
        <h2>Test 5: Current Breakpoint Info</h2>
        <div className="breakpoint-info">
          <p>Resize your browser window to see the responsive behavior!</p>
          <ul>
            <li>📱 Mobile: &lt; 768px</li>
            <li>📱 Tablet: 768px - 1023px</li>
            <li>💻 Desktop: ≥ 1024px</li>
          </ul>
        </div>
      </section>

      <style>{`
        .responsive-test-page {
          padding: var(--space-lg);
          max-width: 1200px;
          margin: 0 auto;
        }

        .test-section {
          margin-bottom: var(--space-xl);
          padding: var(--space-lg);
          border: 2px solid var(--border-light);
          border-radius: 12px;
          background: var(--surface-light);
        }

        .test-section h2 {
          color: var(--primary-600);
          margin-bottom: var(--space-md);
        }

        .test-container {
          border: 1px dashed var(--border-light);
          padding: var(--space-md);
          border-radius: 8px;
        }

        .mobile-test-box {
          background: var(--game-surface);
          padding: var(--space-lg);
          border-radius: 12px;
          text-align: center;
          color: #ffffff;
          border: 2px solid var(--primary-500);
        }

        .desktop-test-box {
          background: linear-gradient(135deg, var(--primary-600), var(--secondary-600));
          padding: var(--space-xl);
          border-radius: 16px;
          text-align: center;
          color: #ffffff;
        }

        .desktop-actions {
          display: flex;
          gap: var(--space-md);
          justify-content: center;
          margin-top: var(--space-md);
        }

        .breakpoint-info {
          background: rgba(102, 126, 234, 0.1);
          padding: var(--space-lg);
          border-radius: 8px;
          border-left: 4px solid var(--primary-500);
        }

        .breakpoint-info ul {
          margin: var(--space-md) 0 0 var(--space-lg);
        }

        .breakpoint-info li {
          margin: var(--space-sm) 0;
          font-family: monospace;
        }
      `}</style>
    </div>
  );
};

export default ResponsiveTestPage;
